import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Loader2 } from 'lucide-react';
import { db } from '@/lib/firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useToast } from '@/hooks/use-toast';

interface HeroData {
  tagline: {
    ar: string;
    en: string;
  };
  title: {
    ar: string;
    en: string;
  };
  titleHighlight: {
    ar: string;
    en: string;
  };
  description: {
    ar: string;
    en: string;
  };
  backgroundImage: string;
  videoUrl: string;
  videoTitle: {
    ar: string;
    en: string;
  };
  primaryButton: {
    text: {
      ar: string;
      en: string;
    };
    link: string;
  };
  secondaryButton: {
    text: {
      ar: string;
      en: string;
    };
    link: string;
  };
}

export function HeroSection() {
  const [heroData, setHeroData] = useState<HeroData>({
    tagline: { ar: '', en: '' },
    title: { ar: '', en: '' },
    titleHighlight: { ar: '', en: '' },
    description: { ar: '', en: '' },
    backgroundImage: '',
    videoUrl: '',
    videoTitle: { ar: '', en: '' },
    primaryButton: {
      text: { ar: '', en: '' },
      link: '',
    },
    secondaryButton: {
      text: { ar: '', en: '' },
      link: '',
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchHeroData();
  }, []);

  const fetchHeroData = async () => {
    try {
      const docRef = doc(db, 'landing_hero', 'main');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setHeroData(docSnap.data() as HeroData);
      }
    } catch (error) {
      console.error('Error fetching hero data:', error);
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء تحميل البيانات',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const docRef = doc(db, 'landing_hero', 'main');
      await setDoc(docRef, {
        ...heroData,
        updatedAt: new Date(),
      });

      toast({
        title: 'تم الحفظ',
        description: 'تم حفظ بيانات القسم الرئيسي بنجاح',
      });
    } catch (error) {
      console.error('Error saving hero data:', error);
      toast({
        title: 'خطأ',
        description: 'حدث خطأ أثناء حفظ البيانات',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="text-center">جاري التحميل...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold">إدارة القسم الرئيسي</h3>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              جاري الحفظ...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              حفظ التغييرات
            </>
          )}
        </Button>
      </div>

      <div className="space-y-6">
        {/* Tagline */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">الشعار / Tagline</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tagline-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Input
                id="tagline-ar"
                value={heroData.tagline.ar}
                onChange={(e) => setHeroData({ ...heroData, tagline: { ...heroData.tagline, ar: e.target.value } })}
                placeholder="نبني العادات الصحية"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tagline-en" className="text-sm text-muted-foreground">English</Label>
              <Input
                id="tagline-en"
                value={heroData.tagline.en}
                onChange={(e) => setHeroData({ ...heroData, tagline: { ...heroData.tagline, en: e.target.value } })}
                placeholder="Building Healthy Habits"
              />
            </div>
          </div>
        </div>

        {/* Title */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">العنوان الرئيسي / Main Title</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Input
                id="title-ar"
                value={heroData.title.ar}
                onChange={(e) => setHeroData({ ...heroData, title: { ...heroData.title, ar: e.target.value } })}
                placeholder="نمط لتعزيز الحياة الصحية"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
              <Input
                id="title-en"
                value={heroData.title.en}
                onChange={(e) => setHeroData({ ...heroData, title: { ...heroData.title, en: e.target.value } })}
                placeholder="Namat for Promoting Healthy Living"
              />
            </div>
          </div>
        </div>

        {/* Title Highlight */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">الكلمة المميزة / Highlight Word</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="highlight-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Input
                id="highlight-ar"
                value={heroData.titleHighlight.ar}
                onChange={(e) => setHeroData({ ...heroData, titleHighlight: { ...heroData.titleHighlight, ar: e.target.value } })}
                placeholder="نمط"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="highlight-en" className="text-sm text-muted-foreground">English</Label>
              <Input
                id="highlight-en"
                value={heroData.titleHighlight.en}
                onChange={(e) => setHeroData({ ...heroData, titleHighlight: { ...heroData.titleHighlight, en: e.target.value } })}
                placeholder="Namat"
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">الوصف / Description</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="description-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Textarea
                id="description-ar"
                value={heroData.description.ar}
                onChange={(e) => setHeroData({ ...heroData, description: { ...heroData.description, ar: e.target.value } })}
                placeholder="نعمل معاً من أجل مجتمع أكثر صحة واستدامة..."
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description-en" className="text-sm text-muted-foreground">English</Label>
              <Textarea
                id="description-en"
                value={heroData.description.en}
                onChange={(e) => setHeroData({ ...heroData, description: { ...heroData.description, en: e.target.value } })}
                placeholder="We work together for a healthier and more sustainable community..."
                rows={4}
              />
            </div>
          </div>
        </div>

        {/* Background Image */}
        <div className="space-y-2">
          <Label htmlFor="backgroundImage">رابط صورة الخلفية / Background Image URL</Label>
          <Input
            id="backgroundImage"
            value={heroData.backgroundImage}
            onChange={(e) => setHeroData({ ...heroData, backgroundImage: e.target.value })}
            placeholder="https://images.unsplash.com/..."
            dir="ltr"
          />
        </div>

        {/* Video URL */}
        <div className="space-y-2">
          <Label htmlFor="videoUrl">رابط الفيديو / Video URL (YouTube Embed)</Label>
          <Input
            id="videoUrl"
            value={heroData.videoUrl}
            onChange={(e) => setHeroData({ ...heroData, videoUrl: e.target.value })}
            placeholder="https://www.youtube.com/embed/..."
            dir="ltr"
          />
        </div>

        {/* Video Title */}
        <div className="space-y-3">
          <Label className="text-base font-semibold">عنوان الفيديو / Video Title</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="videoTitle-ar" className="text-sm text-muted-foreground">عربي</Label>
              <Input
                id="videoTitle-ar"
                value={heroData.videoTitle.ar}
                onChange={(e) => setHeroData({ ...heroData, videoTitle: { ...heroData.videoTitle, ar: e.target.value } })}
                placeholder="نمط لتعزيز الحياة الصحية"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="videoTitle-en" className="text-sm text-muted-foreground">English</Label>
              <Input
                id="videoTitle-en"
                value={heroData.videoTitle.en}
                onChange={(e) => setHeroData({ ...heroData, videoTitle: { ...heroData.videoTitle, en: e.target.value } })}
                placeholder="Namat for Promoting Healthy Living"
              />
            </div>
          </div>
        </div>

        {/* Primary Button */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold">الزر الأساسي / Primary Button</h4>
          <div className="space-y-3">
            <Label className="text-base">نص الزر / Button Text</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="primaryButtonText-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Input
                  id="primaryButtonText-ar"
                  value={heroData.primaryButton.text.ar}
                  onChange={(e) =>
                    setHeroData({
                      ...heroData,
                      primaryButton: { ...heroData.primaryButton, text: { ...heroData.primaryButton.text, ar: e.target.value } },
                    })
                  }
                  placeholder="دخول المستفيد"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="primaryButtonText-en" className="text-sm text-muted-foreground">English</Label>
                <Input
                  id="primaryButtonText-en"
                  value={heroData.primaryButton.text.en}
                  onChange={(e) =>
                    setHeroData({
                      ...heroData,
                      primaryButton: { ...heroData.primaryButton, text: { ...heroData.primaryButton.text, en: e.target.value } },
                    })
                  }
                  placeholder="User Login"
                />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="primaryButtonLink">رابط الزر / Button Link</Label>
            <Input
              id="primaryButtonLink"
              value={heroData.primaryButton.link}
              onChange={(e) =>
                setHeroData({
                  ...heroData,
                  primaryButton: { ...heroData.primaryButton, link: e.target.value },
                })
              }
              placeholder="/login"
              dir="ltr"
            />
          </div>
        </div>

        {/* Secondary Button */}
        <div className="border rounded-lg p-4 space-y-4">
          <h4 className="font-semibold">الزر الثانوي / Secondary Button</h4>
          <div className="space-y-3">
            <Label className="text-base">نص الزر / Button Text</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="secondaryButtonText-ar" className="text-sm text-muted-foreground">عربي</Label>
                <Input
                  id="secondaryButtonText-ar"
                  value={heroData.secondaryButton.text.ar}
                  onChange={(e) =>
                    setHeroData({
                      ...heroData,
                      secondaryButton: { ...heroData.secondaryButton, text: { ...heroData.secondaryButton.text, ar: e.target.value } },
                    })
                  }
                  placeholder="تطوع معنا"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="secondaryButtonText-en" className="text-sm text-muted-foreground">English</Label>
                <Input
                  id="secondaryButtonText-en"
                  value={heroData.secondaryButton.text.en}
                  onChange={(e) =>
                    setHeroData({
                      ...heroData,
                      secondaryButton: { ...heroData.secondaryButton, text: { ...heroData.secondaryButton.text, en: e.target.value } },
                    })
                  }
                  placeholder="Volunteer With Us"
                />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="secondaryButtonLink">رابط الزر / Button Link</Label>
            <Input
              id="secondaryButtonLink"
              value={heroData.secondaryButton.link}
              onChange={(e) =>
                setHeroData({
                  ...heroData,
                  secondaryButton: { ...heroData.secondaryButton, link: e.target.value },
                })
              }
              placeholder="/vol"
              dir="ltr"
            />
          </div>
        </div>
      </div>
    </Card>
  );
}
